import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import BottomNav from "../components/BottomNav";
import { toast } from "sonner";
import { LogOut, BellRing, Shield, Eye, Crown, Activity, Copy, RefreshCw, AlertTriangle, MapPin, LifeBuoy, Trash2, Smartphone, Sparkles } from "lucide-react";
import { useBrowserNotifications, playAlertSound, notify } from "../lib/notifications";
import { isPushSupported, subscribeToPush, unsubscribeFromPush, getPushSubscription } from "../lib/push";
import { Link } from "react-router-dom";
import { Capacitor } from "@capacitor/core";

export default function Settings() {
  const { user, refresh, logout } = useAuth();
  const { permission, request: requestNotif } = useBrowserNotifications();
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [hookInfo, setHookInfo] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [geofences, setGeofences] = useState([]);
  const [billing, setBilling] = useState(null);
  const [shareLocation, setShareLocation] = useState(user?.share_location !== false);
  const [proximityAlerts, setProximityAlerts] = useState(user?.proximity_alerts !== false);
  const [alertSound, setAlertSound] = useState(() => {
    try {
      const raw = localStorage.getItem("proximity:alertSound");
      return raw == null ? true : raw === "true";
    } catch { return true; }
  });
  const [saving, setSaving] = useState(false);
  const [backgroundLocation, setBackgroundLocation] = useState(() => {
    try { return localStorage.getItem("proximity:background-location") === "enabled"; }
    catch { return false; }
  });

  const isNode = user?.account_role === "node";

  useEffect(() => {
    (async () => {
      const s = await getPushSubscription();
      setPushSubscribed(!!s);
      try { const r = await api.get("/health/webhook-info"); setHookInfo(r.data); } catch {}
      try { const c = await api.get("/contacts"); setContacts(c.data || []); } catch {}
      try { const g = await api.get("/geofences"); setGeofences(g.data || []); } catch {}
      try { const b = await api.get("/billing/status"); setBilling(b.data); } catch {}
    })();
  }, [permission]);

  const deleteGeofence = async (id) => {
    try { await api.delete(`/geofences/${id}`); toast.message("Geofence removed"); const g = await api.get("/geofences"); setGeofences(g.data || []); }
    catch { toast.error("Failed"); }
  };

  const setNativeBackgroundLocation = (enabled) => {
    try { localStorage.setItem("proximity:background-location", enabled ? "enabled" : "declined"); } catch {}
    setBackgroundLocation(enabled);
    window.dispatchEvent(new CustomEvent("proximity:background-location", { detail: { enabled } }));
    toast.message(enabled ? "Background sharing enabled" : "Background sharing disabled");
  };

  const copyHook = () => { navigator.clipboard.writeText(hookInfo?.url || ""); toast.success("Webhook URL copied"); };
  const rotateHook = async () => {
    setRotating(true);
    try { await api.post("/health/webhook-rotate"); const r = await api.get("/health/webhook-info"); setHookInfo(r.data); toast.success("URL regenerated"); }
    finally { setRotating(false); }
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (pushSubscribed) {
        await unsubscribeFromPush();
        setPushSubscribed(false);
        toast.message("Background alerts disabled");
      } else {
        const r = await subscribeToPush();
        if (r.ok) {
          setPushSubscribed(true);
          toast.success("Background alerts enabled");
        } else {
          toast.error(`Couldn't enable (${r.reason})`);
        }
      }
    } finally { setPushBusy(false); }
  };

  useEffect(() => {
    if (user) {
      setShareLocation(user.share_location !== false);
      setProximityAlerts(user.proximity_alerts !== false);
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me", {
        share_location: shareLocation,
        proximity_alerts: proximityAlerts,
      });
      await refresh();
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save");
    } finally { setSaving(false); }
  };

  const checkin = async () => {
    try {
      navigator.geolocation.getCurrentPosition(async (pos) => {
        await api.post("/checkins", { note: "I'm safe", lat: pos.coords.latitude, lng: pos.coords.longitude });
        toast.success("Check-in sent to your parents");
      }, () => api.post("/checkins", { note: "I'm safe" }).then(() => toast.success("Check-in sent")));
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-50 pb-28">
      <header className="px-6 pt-10 pb-6">
        <div className="label-eyebrow">Account</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mt-1">Settings</h1>
      </header>

      <section className="px-6">
        <div className="flex items-center gap-4 p-4 rounded-2xl border border-white/10 bg-white/[0.02]">
          <div className="size-14 rounded-full bg-zinc-800 overflow-hidden grid place-items-center font-display font-bold text-xl">
            {user?.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover"/> : (user?.name||"?").slice(0,1)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg truncate">{user?.name}</div>
            <div className="text-sm text-zinc-500 truncate">{user?.email}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={logout}
            className="size-10 rounded-full border border-white/10 grid place-items-center hover:border-[#FF3B30]/40 hover:text-[#FF3B30]"
            title="Sign out"
          >
            <LogOut size={16}/>
          </button>
        </div>
      </section>

      <section className="px-6 mt-6 space-y-3">
        {!isNode && (
          <Link
            to="/billing"
            data-testid="goto-billing"
            className="flex items-center gap-3 p-4 rounded-2xl border border-[#007AFF]/30 bg-gradient-to-br from-[#007AFF]/12 to-transparent hover:from-[#007AFF]/20"
          >
            <div className="size-10 rounded-xl bg-[#007AFF] grid place-items-center">
              <Crown size={18} className="text-white"/>
            </div>
            <div className="flex-1">
              <div className="font-display font-bold" data-testid="tier-label">
                {billing?.tier_label || "Proximity Plus"}
              </div>
              <div className="text-xs text-zinc-400">
                {billing?.is_paid ? `${billing?.device_count || 1} of ${billing?.max_devices || 1} devices · Active`
                 : billing?.in_trial ? `Free trial · ${billing?.days_left_trial}d left`
                 : "Choose a plan to keep tracking your circle"}
              </div>
            </div>
            <span className="text-[#007AFF] font-bold">→</span>
          </Link>
        )}

        {!isNode && billing?.tier !== "elite" && (
          <Link
            to="/billing?tier=elite"
            data-testid="goto-elite"
            className="flex items-center gap-3 p-4 rounded-2xl border border-[#FFCC00]/30 bg-gradient-to-br from-[#FFCC00]/10 to-transparent hover:from-[#FFCC00]/18"
          >
            <div className="size-10 rounded-xl bg-[#FFCC00]/20 grid place-items-center">
              <Sparkles size={18} className="text-[#FFCC00]"/>
            </div>
            <div className="flex-1">
              <div className="font-display font-bold">Upgrade to Proximity Elite</div>
              <div className="text-xs text-zinc-400">Up to 65 devices · $49.99/mo or $499/yr</div>
            </div>
            <span className="text-[#FFCC00] font-bold">→</span>
          </Link>
        )}

        {!isNode && (
          <Link
            to="/devices"
            data-testid="goto-devices"
            className="flex items-center gap-3 p-4 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
          >
            <div className="size-10 rounded-xl bg-[#34C759]/15 grid place-items-center">
              <Smartphone size={18} className="text-[#34C759]"/>
            </div>
            <div className="flex-1">
              <div className="font-display font-bold">Manage My Devices</div>
              <div className="text-xs text-zinc-400">
                {billing ? `${billing.device_count || 1} of ${billing.max_devices || 1} connected` : "Invite or remove devices"}
              </div>
            </div>
            <span className="text-zinc-500">→</span>
          </Link>
        )}

        {isNode && (
          <div
            data-testid="node-banner"
            className="flex items-center gap-3 p-4 rounded-2xl border border-[#34C759]/30 bg-[#34C759]/8"
          >
            <div className="size-10 rounded-xl bg-[#34C759]/20 grid place-items-center">
              <Smartphone size={18} className="text-[#34C759]"/>
            </div>
            <div className="flex-1">
              <div className="font-display font-bold">Connected device</div>
              <div className="text-xs text-zinc-400">You're on a shared plan. Billing is managed by the plan owner.</div>
            </div>
          </div>
        )}
      </section>

      <section className="px-6 mt-3">
        <a
          href="mailto:support@proximity.app?subject=Proximity%20support%20request&body=Hi%20Proximity%20team%2C%0A%0A"
          data-testid="contact-support"
          className="flex items-center gap-3 p-4 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04]"
        >
          <div className="size-10 rounded-xl bg-[#34C759]/15 grid place-items-center">
            <LifeBuoy size={18} className="text-[#34C759]"/>
          </div>
          <div className="flex-1">
            <div className="font-display font-bold">Contact support</div>
            <div className="text-xs text-zinc-400">Bugs, billing, account issues — we usually reply within 24h</div>
          </div>
          <span className="text-zinc-500">→</span>
        </a>
      </section>

      <section className="px-6 mt-6">
        <div className="label-eyebrow mb-2 flex items-center gap-1.5"><MapPin size={12}/> Safe zones</div>
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] divide-y divide-white/5">
          <div className="p-4">
            <div className="font-semibold text-sm mb-1">Set a zone around a contact</div>
            <div className="text-xs text-zinc-500 mb-3">
              Tap a contact below. Drag the radius slider on the map. You&apos;ll get an alert when they enter or leave.
            </div>
            {contacts.length === 0 ? (
              <div className="text-xs text-zinc-600">Add a contact first.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {contacts.map((c) => (
                  <Link
                    key={c.user_id}
                    to={`/geofence/${c.user_id}`}
                    data-testid={`gf-pick-${c.user_id}`}
                    className="flex items-center gap-2 p-2 rounded-xl border border-white/10 hover:bg-white/5 text-sm"
                  >
                    <div className="size-8 rounded-full bg-zinc-800 overflow-hidden grid place-items-center font-display font-bold text-xs">
                      {c.picture ? <img src={c.picture} alt="" className="w-full h-full object-cover" /> : (c.name || "?").slice(0,1)}
                    </div>
                    <span className="truncate flex-1">{c.name}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
          {geofences.length > 0 && (
            <div className="p-4">
              <div className="label-eyebrow mb-2">Active zones · {geofences.length}</div>
              <div className="space-y-1.5">
                {geofences.map((g) => (
                  <div key={g.geofence_id} className="flex items-center gap-2 text-sm">
                    <span className={`size-2.5 rounded-full ${g.enabled === false ? "bg-zinc-600" : "bg-[#FFCC00]"}`}/>
                    <span className="font-black text-xs text-[#FFCC00]">{g.slot || "—"}</span>
                    <Link to={g.contact_user_id ? `/geofence/${g.contact_user_id}` : "/dashboard"} className="flex-1 truncate hover:text-[#007AFF]">{g.name}</Link>
                    <span className="font-mono text-xs text-zinc-500">{g.radius_m}m</span>
                    <button
                      data-testid={`gf-del-${g.geofence_id}`}
                      onClick={() => deleteGeofence(g.geofence_id)}
                      className="size-7 rounded-full grid place-items-center text-zinc-500 hover:text-[#FF3B30]"
                    >
                      <Trash2 size={13}/>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="px-6 mt-6">
        <div className="label-eyebrow mb-2 flex items-center gap-1.5"><Activity size={12}/> Health webhook</div>
        <div className="p-4 rounded-2xl border border-[#FF3B30]/25 bg-[#FF3B30]/5">
          <div className="flex items-start gap-2 text-xs text-[#FF3B30]/90">
            <AlertTriangle size={14} className="shrink-0 mt-0.5"/>
            <div><span className="font-bold">NOT HIPAA certified · NOT a medical device.</span> Use only as a supplemental alert system.</div>
          </div>
          <div className="mt-3 text-xs text-zinc-400 leading-relaxed">
            Forward urgent alerts from Dexcom/Apple Health/Libre via Apple Shortcuts, IFTTT or Zapier. Alerts repeat every 5 min until any emergency contact acknowledges.
          </div>
          {hookInfo && (
            <div className="mt-3 flex items-center gap-2">
              <input data-testid="webhook-url" readOnly value={hookInfo.url} onFocus={(e)=>e.target.select()}
                className="flex-1 bg-zinc-900 border border-white/10 rounded-lg px-3 py-2 text-[11px] font-mono truncate"/>
              <button data-testid="copy-webhook" onClick={copyHook} className="size-9 rounded-lg border border-white/10 grid place-items-center hover:bg-white/5"><Copy size={14}/></button>
              <button data-testid="rotate-webhook" onClick={rotateHook} disabled={rotating} className="size-9 rounded-lg border border-white/10 grid place-items-center hover:bg-white/5 disabled:opacity-50"><RefreshCw size={14}/></button>
            </div>
          )}
          <div className="mt-3 text-[11px] text-zinc-500">POST JSON: <span className="font-mono">{`{"kind":"low_glucose","value":"58","unit":"mg/dL"}`}</span></div>
        </div>
      </section>

      <section className="px-6 mt-6 space-y-3">
        <div className="label-eyebrow mb-1">Privacy</div>
        <Toggle
          icon={Eye} label="Share my location"
          desc="Trusted contacts can see your live position"
          value={shareLocation} onChange={setShareLocation}
          testid="toggle-share-location"
        />
        {Capacitor.isNativePlatform() && (
          <Toggle
            icon={Smartphone}
            label="Background location"
            desc="Keep sharing with accepted contacts while your screen is locked"
            value={backgroundLocation}
            onChange={setNativeBackgroundLocation}
            testid="toggle-background-location"
          />
        )}
        <Toggle
          icon={Shield} label="Proximity alerts (3 mi)"
          desc="Notify me when contacts are within 3 miles"
          value={proximityAlerts} onChange={setProximityAlerts}
          testid="toggle-proximity"
        />
        <Toggle
          icon={BellRing} label="Alert sound"
          desc="Play a soft chime with each proximity alert"
          value={alertSound}
          onChange={(v) => {
            setAlertSound(v);
            try { localStorage.setItem("proximity:alertSound", String(v)); } catch {}
            if (v) playAlertSound("proximity"); // quick preview so users can hear it
          }}
          testid="toggle-alert-sound"
        />
      </section>

      <section className="px-6 mt-6">
        <div className="label-eyebrow mb-2">Notifications</div>
        <div className="p-4 rounded-2xl border border-white/10 bg-white/[0.02]">
          <div className="flex items-center gap-3">
            <BellRing className={permission === "granted" ? "text-[#34C759]" : "text-zinc-500"} size={18}/>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">
                Browser notifications: {" "}
                <span className={
                  permission === "granted" ? "text-[#34C759]" :
                  permission === "denied" ? "text-[#FF3B30]" : "text-[#FFCC00]"
                }>
                  {permission === "granted" ? "Enabled" :
                   permission === "denied" ? "Blocked" :
                   permission === "unsupported" ? "Unsupported" : "Not enabled"}
                </span>
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                {permission === "denied"
                  ? "Re-enable in your browser site settings."
                  : "OS-level pop-ups for proximity, geofence & check-in alerts."}
              </div>
            </div>
            {permission !== "granted" && permission !== "denied" && permission !== "unsupported" && (
              <button
                data-testid="enable-notif-btn"
                onClick={requestNotif}
                className="text-xs font-bold bg-[#007AFF] hover:bg-[#005ec4] px-3 py-2 rounded-full"
              >
                Enable
              </button>
            )}
          </div>

          {permission === "granted" && (
            <div className="mt-4">
              <div className="label-eyebrow mb-2">Preview sounds & icons</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { kind: "proximity", label: "Proximity (3 mi)", color: "#007AFF" },
                  { kind: "geofence_enter", label: "Zone entered", color: "#34C759" },
                  { kind: "geofence_exit", label: "Zone exited", color: "#FF3B30" },
                  { kind: "checkin", label: "Check-in", color: "#FFCC00" },
                ].map((p) => (
                  <button
                    key={p.kind}
                    data-testid={`preview-${p.kind}`}
                    onClick={() => notify(`Preview: ${p.label}`, "This is how the alert will sound and look.", { kind: p.kind, tag: `preview-${p.kind}-${Date.now()}` })}
                    className="flex items-center gap-2 p-2.5 rounded-lg border border-white/10 hover:bg-white/[0.04] text-sm font-semibold text-left"
                  >
                    <span className="size-2.5 rounded-full" style={{ background: p.color }}/>
                    <span className="truncate">{p.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="px-6 mt-6 flex flex-col sm:flex-row gap-3">
        <button
          data-testid="save-settings-btn"
          onClick={save} disabled={saving}
          className="flex-1 bg-[#007AFF] hover:bg-[#005ec4] text-white font-bold py-3 rounded-xl disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button
          data-testid="checkin-btn"
          onClick={checkin}
          className="flex-1 border border-white/10 hover:bg-white/5 font-bold py-3 rounded-xl"
        >
          Send "I'm safe" check-in
        </button>
      </section>

      <BottomNav />
    </div>
  );
}

function Toggle({ icon: Icon, label, desc, value, onChange, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={() => onChange(!value)}
      className="w-full flex items-center gap-3 p-4 rounded-2xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] text-left"
    >
      <Icon className="text-zinc-400" size={18}/>
      <div className="flex-1 min-w-0">
        <div className="font-semibold">{label}</div>
        <div className="text-xs text-zinc-500">{desc}</div>
      </div>
      <span className={`relative h-6 w-11 rounded-full transition-colors ${value ? "bg-[#007AFF]" : "bg-zinc-700"}`}>
        <span className={`absolute top-0.5 ${value ? "right-0.5" : "left-0.5"} size-5 rounded-full bg-white transition-all`}/>
      </span>
    </button>
  );
}
